import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useGlobalContext } from "@/hooks/useGlobalContext";
import useSofaClient from "@/hooks/useSofaClient";
import { COOKIE_BANNER_DISMISSED_COOKIE, deleteCookie, setCookie } from "@/lib/cookie";
import { CONSENT_COOKIE_MAX_AGE_SECONDS } from "@/lib/cookieConsent";

import type { ReactNode } from "react";
import type { CookieConsentSource, CookieConsentValue } from "@/lib/cookieConsent";

type ConsentCategory = "analytics" | "marketing" | "personalization";
type GranularConsent = Record<ConsentCategory, boolean | null>;
type ExplicitLocalConsent = Record<ConsentCategory, boolean>;
type ConsentStatus = "loading" | "resolved" | "error";

type CookieConsent = {
  analyticsConsent?: CookieConsentValue;
  marketingConsent?: CookieConsentValue;
  personalizationConsent?: CookieConsentValue;
  analyticsConsentEnabled: boolean;
  marketingConsentEnabled: boolean;
  personalizationConsentEnabled: boolean;
  isConsentRequired?: boolean;
  isConsentResolved: boolean;
  isGlobalPrivacyControlEnabled: boolean;
  isAuthenticated: boolean;
  globalPrivacyControl: boolean;
  hasExpressedPreference: boolean;
  isCookieBannerDismissed: boolean;
  cookiePreferencesOpen: boolean;
  dismissCookieBanner: () => void;
  setCookiePreferencesOpen: (open: boolean) => void;
  setLocalConsentRequired: (required: boolean) => void;
  updateAnalyticsConsent: (consent: CookieConsentValue) => void;
  updateMarketingConsent: (consent: CookieConsentValue) => void;
  updatePersonalizationConsent: (consent: CookieConsentValue) => void;
  updateAllConsent: (
    consent: CookieConsentValue,
    options?: { includePersonalizationConsent?: boolean },
  ) => void;
};

const CookieConsentContext = createContext<CookieConsent | null>(null);
const EMPTY_GRANULAR_CONSENT: GranularConsent = {
  analytics: null,
  marketing: null,
  personalization: null,
};
const EMPTY_EXPLICIT_LOCAL_CONSENT: ExplicitLocalConsent = {
  analytics: false,
  marketing: false,
  personalization: false,
};
const CONSENT_COOKIE_KEYS = {
  analytics: "analytics_consent",
  marketing: "marketing_consent",
  personalization: "personalization_consent",
} as const;
const CONSENT_SOURCE_COOKIE_KEYS = {
  analytics: "analytics_consent_source",
  marketing: "marketing_consent_source",
  personalization: "personalization_consent_source",
} as const;

function toBooleanConsent(consent?: CookieConsentValue): boolean | null {
  if (consent === "agreed") return true;
  if (consent === "rejected") return false;
  return null;
}

function toCookieConsent(consent: boolean | null): CookieConsentValue | undefined {
  if (consent === true) return "agreed";
  if (consent === false) return "rejected";
  return undefined;
}

function hasGlobalPrivacyControl(): boolean {
  if (typeof navigator === "undefined") return false;
  return (
    (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
  );
}

function resolveConsent(
  canonicalConsent: boolean | null,
  localConsent: boolean | null,
  globalPrivacyControl: boolean,
): boolean | null {
  if (globalPrivacyControl) return false;
  return canonicalConsent ?? localConsent;
}

function effectiveConsent(consent: boolean | null, isConsentRequired: boolean): boolean {
  return consent ?? !isConsentRequired;
}

function cacheConsent(
  category: ConsentCategory,
  consent: boolean | null,
  source?: CookieConsentSource,
) {
  const cookieConsent = toCookieConsent(consent);
  if (cookieConsent == null) return;
  setCookie(CONSENT_COOKIE_KEYS[category], cookieConsent, {
    maxAge: CONSENT_COOKIE_MAX_AGE_SECONDS,
  });
  if (source === "signed_out") {
    setCookie(CONSENT_SOURCE_COOKIE_KEYS[category], source, {
      maxAge: CONSENT_COOKIE_MAX_AGE_SECONDS,
    });
  } else {
    deleteCookie(CONSENT_SOURCE_COOKIE_KEYS[category]);
  }
}

function explicitLocalConsentOnly(
  consent: GranularConsent,
  explicitLocalConsent: ExplicitLocalConsent,
): GranularConsent {
  return {
    analytics: explicitLocalConsent.analytics ? consent.analytics : null,
    marketing: explicitLocalConsent.marketing ? consent.marketing : null,
    personalization: explicitLocalConsent.personalization ? consent.personalization : null,
  };
}

function localConsentAllowedForEffectiveUse(
  consent: GranularConsent,
  explicitLocalConsent: ExplicitLocalConsent,
  legacyRejectedConsent: ExplicitLocalConsent,
): GranularConsent {
  return {
    analytics:
      explicitLocalConsent.analytics || legacyRejectedConsent.analytics ? consent.analytics : null,
    marketing:
      explicitLocalConsent.marketing || legacyRejectedConsent.marketing ? consent.marketing : null,
    personalization:
      explicitLocalConsent.personalization || legacyRejectedConsent.personalization ?
        consent.personalization
      : null,
  };
}

function gatePersonalizationConsent(
  consent: GranularConsent,
  includePersonalizationConsent: boolean,
): GranularConsent {
  return includePersonalizationConsent ? consent : { ...consent, personalization: null };
}

function cacheResolvedConsent(consent: GranularConsent, includePersonalizationConsent: boolean) {
  cacheConsent("analytics", consent.analytics);
  cacheConsent("marketing", consent.marketing);
  if (includePersonalizationConsent) {
    cacheConsent("personalization", consent.personalization);
  }
}

function getAuthorizationHeaders(accessToken: string) {
  return { authorization: "Bearer " + accessToken };
}

export function CookieConsentProvider({
  initialAnalyticsConsent,
  initialMarketingConsent,
  initialPersonalizationConsent,
  initialAnalyticsConsentSource,
  initialMarketingConsentSource,
  initialPersonalizationConsentSource,
  initialCookieBannerDismissed,
  includePersonalizationConsent = true,
  children,
}: {
  initialAnalyticsConsent?: CookieConsentValue;
  initialMarketingConsent?: CookieConsentValue;
  initialPersonalizationConsent?: CookieConsentValue;
  initialAnalyticsConsentSource?: CookieConsentSource;
  initialMarketingConsentSource?: CookieConsentSource;
  initialPersonalizationConsentSource?: CookieConsentSource;
  initialCookieBannerDismissed?: boolean;
  includePersonalizationConsent?: boolean;
  children: ReactNode;
}) {
  const client = useSofaClient();
  const { user } = useGlobalContext();
  const accessToken = user?.accessToken;
  const isAuthenticated = accessToken != null;
  const [localConsent, setLocalConsent] = useState<GranularConsent>({
    analytics: toBooleanConsent(initialAnalyticsConsent),
    marketing: toBooleanConsent(initialMarketingConsent),
    personalization:
      includePersonalizationConsent ? toBooleanConsent(initialPersonalizationConsent) : null,
  });
  const localConsentRef = useRef(localConsent);
  const [explicitLocalConsent, setExplicitLocalConsent] = useState<ExplicitLocalConsent>({
    analytics: initialAnalyticsConsentSource === "signed_out",
    marketing: initialMarketingConsentSource === "signed_out",
    personalization:
      includePersonalizationConsent && initialPersonalizationConsentSource === "signed_out",
  });
  const explicitLocalConsentRef = useRef(explicitLocalConsent);
  // Before source markers existed, rejected cookies were explicit opt-outs while agreed cookies
  // could have been written automatically outside banner-required regions. Preserve only the
  // privacy-safe legacy opt-outs, and never use them as canonical reconciliation input.
  const [legacyRejectedConsent] = useState<ExplicitLocalConsent>({
    analytics: initialAnalyticsConsentSource !== "signed_out" && localConsent.analytics === false,
    marketing: initialMarketingConsentSource !== "signed_out" && localConsent.marketing === false,
    personalization:
      includePersonalizationConsent &&
      initialPersonalizationConsentSource !== "signed_out" &&
      localConsent.personalization === false,
  });
  const legacyRejectedConsentRef = useRef(legacyRejectedConsent);
  const [resolvedConsent, setResolvedConsent] = useState<GranularConsent>(
    isAuthenticated ? EMPTY_GRANULAR_CONSENT : (
      gatePersonalizationConsent(
        localConsentAllowedForEffectiveUse(
          localConsent,
          explicitLocalConsent,
          legacyRejectedConsent,
        ),
        includePersonalizationConsent,
      )
    ),
  );
  const [isConsentRequired, setIsConsentRequired] = useState<boolean>();
  const [localConsentRequired, setLocalConsentRequired] = useState<boolean>();
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>(
    isAuthenticated ? "loading" : "resolved",
  );
  const [isCookieBannerDismissed, setIsCookieBannerDismissed] = useState(
    initialCookieBannerDismissed === true,
  );
  const [cookiePreferencesOpen, setCookiePreferencesOpen] = useState(false);

  useEffect(() => {
    if (!accessToken) {
      setResolvedConsent(
        gatePersonalizationConsent(
          localConsentAllowedForEffectiveUse(
            localConsentRef.current,
            explicitLocalConsentRef.current,
            legacyRejectedConsentRef.current,
          ),
          includePersonalizationConsent,
        ),
      );
      setIsConsentRequired(undefined);
      setConsentStatus("resolved");
      return;
    }

    let isCurrent = true;
    const reconcileConsent = async () => {
      setConsentStatus("loading");
      try {
        const localConsentAtReconciliation = localConsentRef.current;
        const explicitLocalConsentAtReconciliation = explicitLocalConsentRef.current;
        const signedOutLocalConsent = gatePersonalizationConsent(
          explicitLocalConsentOnly(
            localConsentAtReconciliation,
            explicitLocalConsentAtReconciliation,
          ),
          includePersonalizationConsent,
        );
        const privacySafeLocalConsent = gatePersonalizationConsent(
          localConsentAllowedForEffectiveUse(
            localConsentAtReconciliation,
            explicitLocalConsentAtReconciliation,
            legacyRejectedConsentRef.current,
          ),
          includePersonalizationConsent,
        );
        const response = await client.get("/v1/ui/user_consent", {
          headers: getAuthorizationHeaders(accessToken),
        });
        if (response.status !== 200) {
          throw new Error("Unable to load user consent (" + response.status + ")");
        }

        const canonicalConsent = gatePersonalizationConsent(
          response.data.granular_consent ?? EMPTY_GRANULAR_CONSENT,
          includePersonalizationConsent,
        );
        const globalPrivacyControl = hasGlobalPrivacyControl();
        const localFallback: GranularConsent = {
          analytics:
            canonicalConsent.analytics == null && !globalPrivacyControl ?
              signedOutLocalConsent.analytics
            : null,
          marketing:
            canonicalConsent.marketing == null && !globalPrivacyControl ?
              signedOutLocalConsent.marketing
            : null,
          personalization:
            canonicalConsent.personalization == null && !globalPrivacyControl ?
              signedOutLocalConsent.personalization
            : null,
        };
        const hasLocalFallback = Object.values(localFallback).some((value) => value != null);
        let updatedCanonicalConsent = canonicalConsent;

        if (hasLocalFallback) {
          const updateResponse = await client.post("/v1/ui/user_consent", {
            body: { granular_consent: localFallback },
            headers: getAuthorizationHeaders(accessToken),
          });
          if (updateResponse.status !== 200 || !updateResponse.data.is_success) {
            throw new Error("Unable to reconcile user consent (" + updateResponse.status + ")");
          }
          updatedCanonicalConsent = gatePersonalizationConsent(
            updateResponse.data.granular_consent ?? EMPTY_GRANULAR_CONSENT,
            includePersonalizationConsent,
          );
        }

        const nextResolvedConsent: GranularConsent = {
          analytics: resolveConsent(
            updatedCanonicalConsent.analytics,
            privacySafeLocalConsent.analytics,
            globalPrivacyControl,
          ),
          marketing: resolveConsent(
            updatedCanonicalConsent.marketing,
            privacySafeLocalConsent.marketing,
            globalPrivacyControl,
          ),
          personalization: resolveConsent(
            updatedCanonicalConsent.personalization,
            privacySafeLocalConsent.personalization,
            globalPrivacyControl,
          ),
        };

        if (!isCurrent) return;
        setResolvedConsent(nextResolvedConsent);
        setIsConsentRequired(response.data.is_consent_required);
        setConsentStatus("resolved");
        explicitLocalConsentRef.current = EMPTY_EXPLICIT_LOCAL_CONSENT;
        setExplicitLocalConsent(EMPTY_EXPLICIT_LOCAL_CONSENT);
        if (!globalPrivacyControl) {
          cacheResolvedConsent(nextResolvedConsent, includePersonalizationConsent);
        }
      } catch (error) {
        if (!isCurrent) return;
        console.error("Unable to reconcile user consent", error);
        setResolvedConsent(EMPTY_GRANULAR_CONSENT);
        setIsConsentRequired(true);
        setConsentStatus("error");
      }
    };

    void reconcileConsent();
    return () => {
      isCurrent = false;
    };
  }, [accessToken, client, includePersonalizationConsent]);

  const updateConsentValues = useCallback(
    (consentUpdates: Partial<Record<ConsentCategory, CookieConsentValue>>) => {
      const consentValues = Object.fromEntries(
        Object.entries(consentUpdates)
          .map(([category, consent]) => [category, toBooleanConsent(consent)])
          .filter(([, consent]) => consent != null),
      ) as Partial<Record<ConsentCategory, boolean>>;
      if (!includePersonalizationConsent) {
        delete consentValues.personalization;
      }
      const updatedCategories = Object.keys(consentValues) as ConsentCategory[];
      if (updatedCategories.length === 0) return;

      const nextLocalConsent = gatePersonalizationConsent(
        { ...localConsentRef.current, ...consentValues },
        includePersonalizationConsent,
      );
      localConsentRef.current = nextLocalConsent;
      setLocalConsent(nextLocalConsent);
      if (!accessToken) {
        const nextExplicitLocalConsent = { ...explicitLocalConsentRef.current };
        for (const category of updatedCategories) {
          nextExplicitLocalConsent[category] = true;
          cacheConsent(category, consentValues[category]!, "signed_out");
        }
        explicitLocalConsentRef.current = nextExplicitLocalConsent;
        setExplicitLocalConsent(nextExplicitLocalConsent);
        setResolvedConsent(
          gatePersonalizationConsent(
            localConsentAllowedForEffectiveUse(
              nextLocalConsent,
              nextExplicitLocalConsent,
              legacyRejectedConsentRef.current,
            ),
            includePersonalizationConsent,
          ),
        );
        return;
      }

      for (const category of updatedCategories) {
        cacheConsent(category, consentValues[category]!);
      }

      setConsentStatus("loading");
      void client
        .post("/v1/ui/user_consent", {
          body: { granular_consent: consentValues },
          headers: getAuthorizationHeaders(accessToken),
        })
        .then((response) => {
          if (response.status !== 200 || !response.data.is_success) {
            throw new Error("Unable to update user consent (" + response.status + ")");
          }
          const canonicalConsent = gatePersonalizationConsent(
            response.data.granular_consent ?? EMPTY_GRANULAR_CONSENT,
            includePersonalizationConsent,
          );
          const globalPrivacyControl = hasGlobalPrivacyControl();
          const privacySafeLocalConsent = gatePersonalizationConsent(
            localConsentAllowedForEffectiveUse(
              nextLocalConsent,
              explicitLocalConsentRef.current,
              legacyRejectedConsentRef.current,
            ),
            includePersonalizationConsent,
          );
          const nextResolvedConsent: GranularConsent = {
            analytics: resolveConsent(
              canonicalConsent.analytics,
              privacySafeLocalConsent.analytics,
              globalPrivacyControl,
            ),
            marketing: resolveConsent(
              canonicalConsent.marketing,
              privacySafeLocalConsent.marketing,
              globalPrivacyControl,
            ),
            personalization: resolveConsent(
              canonicalConsent.personalization,
              privacySafeLocalConsent.personalization,
              globalPrivacyControl,
            ),
          };
          setResolvedConsent(nextResolvedConsent);
          setConsentStatus("resolved");
          if (!globalPrivacyControl) {
            cacheResolvedConsent(nextResolvedConsent, includePersonalizationConsent);
          }
        })
        .catch((error) => {
          console.error("Unable to update user consent", error);
          setResolvedConsent(EMPTY_GRANULAR_CONSENT);
          setIsConsentRequired(true);
          setConsentStatus("error");
        });
    },
    [accessToken, client, includePersonalizationConsent],
  );

  const updateAnalyticsConsent = useCallback(
    (consent: CookieConsentValue) => updateConsentValues({ analytics: consent }),
    [updateConsentValues],
  );
  const updateMarketingConsent = useCallback(
    (consent: CookieConsentValue) => updateConsentValues({ marketing: consent }),
    [updateConsentValues],
  );
  const updatePersonalizationConsent = useCallback(
    (consent: CookieConsentValue) => updateConsentValues({ personalization: consent }),
    [updateConsentValues],
  );
  const updateAllConsent = useCallback(
    (
      consent: CookieConsentValue,
      {
        includePersonalizationConsent:
          includePersonalizationConsentUpdate = includePersonalizationConsent,
      }: { includePersonalizationConsent?: boolean } = {},
    ) =>
      updateConsentValues({
        analytics: consent,
        marketing: consent,
        ...(includePersonalizationConsentUpdate ? { personalization: consent } : {}),
      }),
    [includePersonalizationConsent, updateConsentValues],
  );
  const dismissCookieBanner = useCallback(() => {
    setIsCookieBannerDismissed(true);
    setCookie(COOKIE_BANNER_DISMISSED_COOKIE, "true", {
      maxAge: null,
    });
  }, []);

  const isConsentResolved = consentStatus === "resolved";
  const required = isAuthenticated ? isConsentRequired ?? true : localConsentRequired ?? true;
  const globalPrivacyControl = hasGlobalPrivacyControl();
  const value = useMemo<CookieConsent>(
    () => ({
      analyticsConsent: toCookieConsent(resolvedConsent.analytics),
      marketingConsent: toCookieConsent(resolvedConsent.marketing),
      personalizationConsent:
        includePersonalizationConsent ?
          toCookieConsent(resolvedConsent.personalization)
        : undefined,
      analyticsConsentEnabled:
        !globalPrivacyControl &&
        isConsentResolved &&
        effectiveConsent(resolvedConsent.analytics, required),
      marketingConsentEnabled:
        !globalPrivacyControl &&
        isConsentResolved &&
        effectiveConsent(resolvedConsent.marketing, required),
      personalizationConsentEnabled:
        includePersonalizationConsent &&
        !globalPrivacyControl &&
        isConsentResolved &&
        effectiveConsent(resolvedConsent.personalization, required),
      isConsentRequired: isAuthenticated ? isConsentRequired : localConsentRequired,
      isConsentResolved,
      isGlobalPrivacyControlEnabled: globalPrivacyControl,
      isAuthenticated,
      globalPrivacyControl,
      hasExpressedPreference:
        explicitLocalConsent.analytics ||
        explicitLocalConsent.marketing ||
        (includePersonalizationConsent && explicitLocalConsent.personalization) ||
        legacyRejectedConsent.analytics ||
        legacyRejectedConsent.marketing ||
        (includePersonalizationConsent && legacyRejectedConsent.personalization),
      isCookieBannerDismissed,
      cookiePreferencesOpen,
      dismissCookieBanner,
      setCookiePreferencesOpen,
      setLocalConsentRequired,
      updateAnalyticsConsent,
      updateMarketingConsent,
      updatePersonalizationConsent,
      updateAllConsent,
    }),
    [
      resolvedConsent,
      globalPrivacyControl,
      isConsentResolved,
      required,
      isConsentRequired,
      localConsentRequired,
      isAuthenticated,
      explicitLocalConsent,
      legacyRejectedConsent,
      isCookieBannerDismissed,
      cookiePreferencesOpen,
      dismissCookieBanner,
      updateAnalyticsConsent,
      updateMarketingConsent,
      updatePersonalizationConsent,
      updateAllConsent,
      includePersonalizationConsent,
    ],
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent(): CookieConsent {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error("useCookieConsent must be used within a CookieConsentProvider");
  }
  return context;
}
